import React, { useEffect, useMemo, useState } from "react";
import "./AdminPage.css";
import { useFirebase } from "../contexts/FirebaseContext";
import {
  getFilamentHex,
  getFilamentLabel,
  isFilamentLight,
  isFilamentRainbow,
} from "../constants/filamentColors";
import AdminMissionsEditor from "./AdminMissionsEditor";
import AdminOnlinePosts from "./AdminOnlinePosts";
import AdminLeaderboards from "./AdminLeaderboards";

// Change this password to control admin access.
const ADMIN_PASSWORD = "12345678987654321";
const ADMIN_SESSION_KEY = "arcadeAdminAuthed";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return value.toLocaleString();
  } catch {
    return "—";
  }
};

const CLAIM_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "on_the_way", label: "On the way" },
  { value: "shipped", label: "Shipped" },
];

const PencilIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const AdminPage = () => {
  const {
    getAllUsers,
    adminUpdateUserPoints,
    getAllRewardClaims,
    updateRewardClaimStatus,
    deleteRewardClaim,
    getAllProblemReports,
    deleteProblemReport,
  } = useFirebase();
  const [authed, setAuthed] = useState(
    () => sessionStorage.getItem(ADMIN_SESSION_KEY) === "1"
  );
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [claims, setClaims] = useState([]);
  const [reports, setReports] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [claimsError, setClaimsError] = useState("");
  const [reportsError, setReportsError] = useState("");
  const [updatingClaimId, setUpdatingClaimId] = useState(null);
  const [deletingClaimId, setDeletingClaimId] = useState(null);
  const [deletingReportId, setDeletingReportId] = useState(null);
  const [editingPointsUser, setEditingPointsUser] = useState(null);
  const [pointsDraft, setPointsDraft] = useState("");
  const [pointsModalError, setPointsModalError] = useState("");
  const [savingPoints, setSavingPoints] = useState(false);
  const [usersMessage, setUsersMessage] = useState("");

  useEffect(() => {
    if (!authed) return undefined;

    let cancelled = false;
    const load = async () => {
      setLoadingUsers(true);
      setLoadingClaims(true);
      setLoadingReports(true);
      setLoadError("");
      setClaimsError("");
      setReportsError("");

      const [usersResult, claimsResult, reportsResult] = await Promise.allSettled([
        getAllUsers(),
        getAllRewardClaims(),
        getAllProblemReports(),
      ]);

      if (cancelled) return;

      if (usersResult.status === "fulfilled") {
        const sorted = [...usersResult.value].sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bTime - aTime;
        });
        setUsers(sorted);
      } else {
        console.error(usersResult.reason);
        setLoadError("Could not load users. Check Firebase permissions.");
      }

      if (claimsResult.status === "fulfilled") {
        setClaims(
          [...claimsResult.value].sort((a, b) => {
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bTime - aTime;
          })
        );
      } else {
        console.error(claimsResult.reason);
        setClaimsError(
          "Could not load reward claims. Check Firebase permissions."
        );
      }

      if (reportsResult.status === "fulfilled") {
        setReports(
          [...reportsResult.value].sort((a, b) => {
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bTime - aTime;
          })
        );
      } else {
        console.error(reportsResult.reason);
        setReportsError(
          "Could not load problem reports. Check Firebase permissions."
        );
      }

      setLoadingUsers(false);
      setLoadingClaims(false);
      setLoadingReports(false);
    };

    load();
    return () => {
      cancelled = true;
    };
    // Intentionally run once per auth session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const stats = useMemo(() => {
    const totalUsers = users.length;
    const totalPoints = users.reduce((sum, user) => sum + (user.points || 0), 0);
    const namedUsers = users.filter((user) => user.displayName).length;
    const averagePoints =
      totalUsers > 0 ? Math.round(totalPoints / totalUsers) : 0;
    const pendingClaims = claims.filter(
      (claim) => claim.status === "pending"
    ).length;
    return {
      totalUsers,
      totalPoints,
      namedUsers,
      averagePoints,
      pendingClaims,
      totalClaims: claims.length,
      totalReports: reports.length,
    };
  }, [users, claims, reports]);

  const handleLogin = (event) => {
    event.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem(ADMIN_SESSION_KEY, "1");
      setAuthed(true);
      setError("");
      setPassword("");
      return;
    }
    setError("Incorrect password.");
  };

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    setAuthed(false);
    setUsers([]);
    setClaims([]);
    setReports([]);
    setEditingPointsUser(null);
    setPointsDraft("");
    setPointsModalError("");
    setUsersMessage("");
  };

  const openPointsModal = (user) => {
    setEditingPointsUser(user);
    setPointsDraft(String(user.points ?? 0));
    setPointsModalError("");
    setUsersMessage("");
  };

  const closePointsModal = () => {
    if (savingPoints) return;
    setEditingPointsUser(null);
    setPointsDraft("");
    setPointsModalError("");
  };

  const handleSaveUserPoints = async (event) => {
    event.preventDefault();
    if (!editingPointsUser) return;

    const nextPoints = Math.max(0, Math.floor(Number(pointsDraft)));
    if (Number.isNaN(Number(pointsDraft)) || pointsDraft.trim() === "") {
      setPointsModalError("Enter a valid number of points.");
      return;
    }

    setSavingPoints(true);
    setPointsModalError("");
    setLoadError("");
    try {
      await adminUpdateUserPoints(editingPointsUser.userId, nextPoints);
      setUsers((current) =>
        current.map((row) =>
          row.userId === editingPointsUser.userId
            ? { ...row, points: nextPoints }
            : row
        )
      );
      setUsersMessage(
        `Updated points for ${
          editingPointsUser.displayName || editingPointsUser.userId
        } to ${nextPoints}.`
      );
      setEditingPointsUser(null);
      setPointsDraft("");
    } catch (err) {
      console.error(err);
      setPointsModalError(
        "Could not update user points. Check Firebase permissions."
      );
    } finally {
      setSavingPoints(false);
    }
  };

  const handleClaimStatusChange = async (claimId, nextStatus) => {
    const previousStatus = claims.find((claim) => claim.id === claimId)?.status;
    setClaims((current) =>
      current.map((claim) =>
        claim.id === claimId ? { ...claim, status: nextStatus } : claim
      )
    );
    setUpdatingClaimId(claimId);
    setClaimsError("");

    try {
      await updateRewardClaimStatus(claimId, nextStatus);
    } catch (err) {
      console.error(err);
      setClaims((current) =>
        current.map((claim) =>
          claim.id === claimId
            ? { ...claim, status: previousStatus || "pending" }
            : claim
        )
      );
      setClaimsError("Could not update claim status. Try again.");
    } finally {
      setUpdatingClaimId(null);
    }
  };

  const handleDeleteClaim = async (claim) => {
    const label = claim.rewardName || claim.rewardId || "this order";
    const confirmed = window.confirm(
      `Delete order for "${label}"? This cannot be undone.`
    );
    if (!confirmed) return;

    const previousClaims = claims;
    setClaims((current) => current.filter((item) => item.id !== claim.id));
    setDeletingClaimId(claim.id);
    setClaimsError("");

    try {
      await deleteRewardClaim(claim.id, { admin: true });
    } catch (err) {
      console.error(err);
      setClaims(previousClaims);
      setClaimsError("Could not delete order. Try again.");
    } finally {
      setDeletingClaimId(null);
    }
  };

  const handleDeleteReport = async (report) => {
    const confirmed = window.confirm(
      "Delete this problem report? This cannot be undone."
    );
    if (!confirmed) return;

    const previousReports = reports;
    setReports((current) => current.filter((item) => item.id !== report.id));
    setDeletingReportId(report.id);
    setReportsError("");

    try {
      await deleteProblemReport(report.id);
    } catch (err) {
      console.error(err);
      setReports(previousReports);
      setReportsError("Could not delete report. Try again.");
    } finally {
      setDeletingReportId(null);
    }
  };

  if (!authed) {
    return (
      <div className="admin-page">
        <form className="admin-login-card" onSubmit={handleLogin}>
          <p className="admin-eyebrow">Starworks</p>
          <h1 className="admin-title">Admin access</h1>
          <p className="admin-sub">
            Enter the admin password to view user analytics.
          </p>
          <label className="admin-label" htmlFor="admin-password">
            Password
          </label>
          <input
            id="admin-password"
            className="admin-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus
          />
          {error && <p className="admin-error">{error}</p>}
          <button type="submit" className="admin-button">
            Enter dashboard
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-page admin-page--dashboard">
      <header className="admin-header">
        <div>
          <p className="admin-eyebrow">Starworks Admin</p>
          <h1 className="admin-title">Analytics dashboard</h1>
          <p className="admin-sub">
            Production Firebase — live users &amp; points (not localhost)
          </p>
        </div>
        <div className="admin-header-actions">
          <AdminOnlinePosts />
          <AdminLeaderboards />
          <button
            type="button"
            className="admin-button admin-button--ghost"
            onClick={() => {
              const target =
                document.getElementById("admin-mission-first") ||
                document.getElementById("admin-missions");
              target?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            Go to Missions
          </button>
          <button
            type="button"
            className="admin-button admin-button--ghost"
            onClick={() => window.location.assign("/")}
          >
            Back to app
          </button>
          <button
            type="button"
            className="admin-button admin-button--ghost"
            onClick={handleLogout}
          >
            Log out
          </button>
        </div>
      </header>

      <section className="admin-stats">
        <div className="admin-stat-card">
          <div className="admin-stat-label">Users created</div>
          <div className="admin-stat-value">{stats.totalUsers}</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-label">Named profiles</div>
          <div className="admin-stat-value">{stats.namedUsers}</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-label">Total points</div>
          <div className="admin-stat-value">{stats.totalPoints}</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-label">Pending claims</div>
          <div className="admin-stat-value">{stats.pendingClaims}</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-label">Problem reports</div>
          <div className="admin-stat-value">{stats.totalReports}</div>
        </div>
      </section>

      <section className="admin-table-card">
        <div className="admin-table-header">
          <h2>Problem reports</h2>
          {loadingReports && <span className="admin-muted">Loading…</span>}
          {!loadingReports && (
            <span className="admin-muted">{stats.totalReports} total</span>
          )}
        </div>

        {reportsError && <p className="admin-error">{reportsError}</p>}

        {!loadingReports && reports.length === 0 && !reportsError && (
          <p className="admin-muted">No problem reports yet.</p>
        )}

        {reports.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table admin-table--reports">
              <thead>
                <tr>
                  <th>Message</th>
                  <th>User</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.id}>
                    <td className="admin-report-message">
                      {report.message || "—"}
                    </td>
                    <td className="admin-userid">
                      {report.displayName || report.userId || "—"}
                    </td>
                    <td>{formatDate(report.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-claim-delete"
                        disabled={deletingReportId === report.id}
                        onClick={() => handleDeleteReport(report)}
                      >
                        {deletingReportId === report.id
                          ? "Deleting…"
                          : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="admin-table-card">
        <div className="admin-table-header">
          <h2>Reward claims</h2>
          {loadingClaims && <span className="admin-muted">Loading…</span>}
          {!loadingClaims && (
            <span className="admin-muted">{stats.totalClaims} total</span>
          )}
        </div>

        {claimsError && <p className="admin-error">{claimsError}</p>}

        {!loadingClaims && claims.length === 0 && !claimsError && (
          <p className="admin-muted">No reward claims yet.</p>
        )}

        {claims.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table admin-table--claims">
              <thead>
                <tr>
                  <th>Reward</th>
                  <th>Color</th>
                  <th>Ship to</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>User</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {claims.map((claim) => (
                  <tr key={claim.id}>
                    <td>{claim.rewardName || claim.rewardId || "—"}</td>
                    <td>
                      <span className="admin-claim-color">
                        <span
                          className={`admin-claim-swatch ${
                            isFilamentLight(claim.color) ? "is-light" : ""
                          } ${isFilamentRainbow(claim.color) ? "is-rainbow" : ""}`}
                          style={
                            isFilamentRainbow(claim.color)
                              ? undefined
                              : {
                                  backgroundColor: getFilamentHex(claim.color),
                                }
                          }
                          aria-hidden="true"
                        />
                        {getFilamentLabel(claim.color)}
                      </span>
                    </td>
                    <td>{claim.shippingName || "—"}</td>
                    <td className="admin-claim-address">
                      {claim.address || "—"}
                    </td>
                    <td>
                      <select
                        className={`admin-claim-status-select admin-claim-status--${
                          claim.status || "pending"
                        }`}
                        value={claim.status || "pending"}
                        disabled={updatingClaimId === claim.id}
                        onChange={(e) =>
                          handleClaimStatusChange(claim.id, e.target.value)
                        }
                        aria-label={`Status for ${
                          claim.rewardName || claim.rewardId || "claim"
                        }`}
                      >
                        {CLAIM_STATUSES.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{formatDate(claim.createdAt)}</td>
                    <td className="admin-userid">
                      {claim.displayName || claim.userId || "—"}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="admin-claim-delete"
                        disabled={
                          deletingClaimId === claim.id ||
                          updatingClaimId === claim.id
                        }
                        onClick={() => handleDeleteClaim(claim)}
                      >
                        {deletingClaimId === claim.id ? "Deleting…" : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="admin-table-card">
        <div className="admin-table-header">
          <h2>Users & points</h2>
          {loadingUsers && <span className="admin-muted">Loading…</span>}
        </div>

        {loadError && <p className="admin-error">{loadError}</p>}
        {usersMessage && <p className="admin-success">{usersMessage}</p>}

        {!loadingUsers && users.length === 0 && !loadError && (
          <p className="admin-muted">No users found yet.</p>
        )}

        {users.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Display name</th>
                  <th>Points</th>
                  <th>Language</th>
                  <th>Created</th>
                  <th>User ID</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.userId}>
                    <td>{user.displayName || "—"}</td>
                    <td>
                      <div className="admin-points-cell">
                        <span className="admin-points">{user.points}</span>
                        <button
                          type="button"
                          className="admin-points-edit"
                          onClick={() => openPointsModal(user)}
                          aria-label={`Edit points for ${
                            user.displayName || user.userId
                          }`}
                          title="Edit points"
                        >
                          <PencilIcon />
                        </button>
                      </div>
                    </td>
                    <td>{(user.language || "en").toUpperCase()}</td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td className="admin-userid">{user.userId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editingPointsUser && (
        <div className="admin-modal-overlay" onClick={closePointsModal}>
          <div
            className="admin-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-points-modal-title"
          >
            <h3 id="admin-points-modal-title">Edit points</h3>
            <p className="admin-modal-sub">
              {editingPointsUser.displayName || "Unnamed user"}
              <span className="admin-modal-userid">
                {editingPointsUser.userId}
              </span>
            </p>

            <form className="admin-modal-form" onSubmit={handleSaveUserPoints}>
              <label className="admin-label" htmlFor="admin-edit-points">
                Stars / points
              </label>
              <input
                id="admin-edit-points"
                className="admin-input"
                type="number"
                min={0}
                step={1}
                value={pointsDraft}
                onChange={(e) => {
                  setPointsDraft(e.target.value);
                  setPointsModalError("");
                }}
                disabled={savingPoints}
                autoFocus
              />

              {pointsModalError && (
                <p className="admin-error">{pointsModalError}</p>
              )}

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-button admin-button--ghost"
                  onClick={closePointsModal}
                  disabled={savingPoints}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-button"
                  disabled={savingPoints}
                >
                  {savingPoints ? "Saving…" : "Save points"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AdminMissionsEditor />
    </div>
  );
};

export default AdminPage;
